/* eslint-disable @akinon/projectzero/case-warning */
'use client';

import { Fragment } from 'react';
import { Icon, Link } from '@theme/components';
import { ROUTES } from '@theme/routes';
import { useLocalization } from '@akinon/next/hooks';
import { usePathname } from 'next/navigation';
import { BreadcrumbResultType } from '@akinon/next/types';
import { capitalize } from '@akinon/next/utils';
import { generateBreadcrumbJsonLd } from '@theme/utils/generate-jsonld';
import clsx from 'clsx';

export interface BreadcrumbProps {
  breadcrumbList?: extendedBreadcrumbType[];
  title?: string;
  className?: string;
}

type extendedBreadcrumbType = {
  extra_context?: {
    attributes?: {
      hide_from_breadcrumb?: {
        value: 'true' | 'false';
      };
    };
  };
} & BreadcrumbResultType;

export default function Breadcrumb(props: BreadcrumbProps) {
  const { t } = useLocalization();
  const pathname = usePathname();
  const { breadcrumbList = [], className } = props;

  const list = [
    { url: ROUTES.HOME, text: t('common.breadcrumb.homepage') },
    ...breadcrumbList
      .filter(
        (breadcrumb) =>
          breadcrumb?.extra_context?.attributes?.category_id !== null &&
          breadcrumb?.extra_context?.attributes?.hide_from_breadcrumb?.value !==
            'true'
      )
      .map((breadcrumb) => ({
        url: breadcrumb.url,
        text: breadcrumb.label
      })),
    ...(props.title ? [{ text: props.title }] : [])
  ];

  const breadcrumbJsonLd = generateBreadcrumbJsonLd(
    list.map((item, index) => ({
      url:
        'url' in item && item.url
          ? item.url
          : index === list.length - 1
            ? pathname
            : undefined,
      text: capitalize(item.text.toLocaleLowerCase())
    }))
  );

  return (
    <div
      className={clsx([
        className,
        'flex items-center text-primary-black text-xs space-x-2 py-5 overflow-x-auto flex-nowrap md:text-gray capitalize'
      ])}
    >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />
      {list.map((item, index) => (
        <Fragment key={index}>
          {'url' in item && item.url && index !== list.length - 1 ? (
            <Link href={item.url} className="whitespace-nowrap">
              {capitalize(item.text.toLocaleLowerCase())}
            </Link>
          ) : (
            <span className="whitespace-nowrap">{item.text}</span>
          )}
          {index !== list.length - 1 && <Icon name="chevron-end" size={18} />}
        </Fragment>
      ))}
    </div>
  );
}
